import {
  ComboboxProvider,
  Combobox as AriakitCombobox,
  ComboboxPopover,
  ComboboxItem,
  ComboboxList,
} from '@ariakit/react'
import { startTransition, useDeferredValue, useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'

interface SearchableComboboxProps {
  value: string
  onValueChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  placeholder?: string
}

const inputBase =
  'flex h-10 w-full rounded-[var(--radius)] border border-[var(--color-input)] bg-[var(--color-background)] pl-3 pr-8 py-2 text-sm placeholder:text-[var(--color-muted-foreground)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]'
const itemBase =
  'flex cursor-pointer items-center px-4 py-2 text-sm text-[var(--color-foreground)] data-[active-item]:bg-[color-mix(in_oklab,var(--color-accent)_12%,transparent)]'
const itemSelected = 'text-[var(--color-accent)] font-medium'

export function Combobox({
  value,
  onValueChange,
  options,
  placeholder = 'Search Specialty',
}: SearchableComboboxProps) {
  const selected = options.find((o) => o.value === value)
  const [searchValue, setSearchValue] = useState(selected?.label ?? '')
  const [open, setOpen] = useState(false)
  const deferredSearch = useDeferredValue(searchValue)

  const filtered = useMemo(() => {
    const t = deferredSearch.trim().toLowerCase()
    return t
      ? options.filter((o) => o.label.toLowerCase().includes(t))
      : options
  }, [options, deferredSearch])

  const displayValue = open ? searchValue : (selected?.label ?? '')

  return (
    <div className="relative w-full">
      <ComboboxProvider
        value={displayValue}
        setValue={(v) => {
          const opt = options.find((o) => o.value === v)
          if (opt) {
            onValueChange(opt.value)
            setSearchValue(opt.label)
          } else {
            startTransition(() => setSearchValue(v))
          }
        }}
        selectedValue={value}
        setSelectedValue={(id) => {
          const opt = options.find((o) => o.value === id)
          if (opt) {
            onValueChange(id)
            setSearchValue(opt.label)
          }
        }}
        resetValueOnHide={false}
        setOpen={(next) => {
          setOpen(next)
          if (!next) setSearchValue(selected?.label ?? '')
        }}
      >
        <AriakitCombobox
          placeholder={placeholder}
          className={inputBase}
        />
        <ChevronDown
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]"
          aria-hidden
        />
        <ComboboxPopover
          gutter={4}
          sameWidth
          className="z-[9999] mt-1 max-h-72 overflow-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-lg"
        >
          <ComboboxList>
            {filtered.length === 0 ? (
              <div className="px-4 py-2 text-sm text-[var(--color-muted-foreground)]">
                No results
              </div>
            ) : (
              filtered.map((opt) => (
                <ComboboxItem
                  key={opt.value}
                  value={opt.value}
                  className={`${itemBase} ${opt.value === value ? itemSelected : ''}`.trim()}
                  focusOnHover
                >
                  {opt.label}
                </ComboboxItem>
              ))
            )}
          </ComboboxList>
        </ComboboxPopover>
      </ComboboxProvider>
    </div>
  )
}
